/**************************************************
 * 
 * Global QMod Component Changes
 * 
 * These are temporary hotfixes until QMod makes official updates.
 * 
 **************************************************/

/**
 * Function to delay execution by a given time in milliseconds.
 */
function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Centralized function to apply all QMod accessibility fixes.
 * Called on initial load and after browser resize.
 * 
 * @since CT-784
 */
async function applyAllQmodAccessibilityFixes() {
    jq('[id^=highcharts-].highcharts-container').attr("role", "region");
    assignUniqueAriaLabels();
    assignUniqueFilterIds();
    jq('[id^=highcharts-end-of-chart-marker-]').attr("role", "region");
    
    await Promise.all([
        assignUniqueAttributes(),
        fixEmptyTableHeaders(),
        addTabIndexToVolumeToggle(),
        fixInvalidAriaAttributes(),
        assignUniqueLabels(),
        fixButtonAriaLabels()
    ]);
    
    setAriaLabelsForIcons();
}

function fixButtonAriaLabels() {
    const buttons = document.querySelectorAll('.qmod-btn');
    buttons.forEach((btn) => {
        let label = btn.getAttribute('aria-label');
        if (!label) {
            return;
        }
        let btn_text = btn.querySelector('.qmod-btn-text').innerText;
        if (!label.includes(btn_text)) {
            label += `, ${btn_text}`;
            btn.setAttribute('aria-label', label);
        }
    });
}

/**
 * Debounce utility to prevent excessive function calls during resize.
 * 
 * @since CT-784
 */
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

/**
 * Reapply QMod accessibility fixes on browser resize.
 * Uses debounce to wait until resize is complete.
 * 
 * @since CT-784
 */
const reapplyQmodFixesOnResize = debounce(function() {
    applyAllQmodAccessibilityFixes();
}, 250);

window.addEventListener('resize', reapplyQmodFixesOnResize);

/**
 * Page Type: QMod - Chart, Quote & Historical 
 * 
 * Assigns unique aria-labels to highcharts containers.
 * 
 * @since QMod v1.69.0
 */
function assignUniqueAriaLabels() {
    const charts = jq('[id^=highcharts-].highcharts-container');
    charts.each(function (index) {
        const uniqueLabel = `Chart Container ${index + 1}`;
        jq(this).attr("aria-label", uniqueLabel);
    });
}

/**
 * Page Type: QMod - Chart, Quote & Historical 
 * 
 * Assigns unique IDs to duplicate filters within SVG defs.
 * 
 * @since QMod v1.69.0
 */
function assignUniqueFilterIds() {
    const filters = jq('svg defs filter[id="highcharts-drop-shadow-0"]');
    filters.each(function (index) {
        if (index > 0) {
            const newId = `highcharts-drop-shadow-${Math.random().toString(36).substr(2, 9)}`;
            jq(this).attr('id', newId);

            // Update any references to this filter id in the SVG
            const references = jq(`svg [filter="url(#highcharts-drop-shadow-0)"]`);
            references.each(function () {
                jq(this).attr('filter', `url(#${newId})`);
            });
        }
    });
}

// Call toggleTabIndex and apply all accessibility fixes once QMod content is ready
observeForContent('.highcharts-container').then(() => {
    toggleTabIndex(null, true);
    applyAllQmodAccessibilityFixes();
});

/**
 * Page Type: QMod - Chart, Quote & Historical 
 * 
 * Fixes the unique ID and missing aria attributes.
 * 
 * @since QMod v1.69.0
 */
function assignUniqueAttributes() {
    return new Promise((resolve) => {
        function generateUniqueId(baseId) {
            return baseId + '-' + Math.random().toString(36).substr(2, 9);
        }

        const ids = [
            'highcharts-end-of-chart-marker-0',
            'highcharts-screen-reader-region-before-0',
            'highcharts-drop-shadow-0',
            'highcharts-screen-reader-region-after-0',
            'highcharts-2ih7fo0-0',
            'highcharts-fjy84ra-0'
        ];

        ids.forEach((baseId) => {
            const elements = document.querySelectorAll(`#${baseId}`);
            elements.forEach((element, index) => {
                if (index > 0) {
                    const newId = generateUniqueId(baseId);
                    element.setAttribute('id', newId);
                }
            });
        });

        const landmarkElements = document.querySelectorAll('.highcharts-exit-anchor, .highcharts-container');
        landmarkElements.forEach((element, index) => {
            if (!element.getAttribute('aria-label')) {
                element.setAttribute('aria-label', `Unique Chart ${index + 1}`);
            }
        });

        const filters = document.querySelectorAll('svg defs filter[id="highcharts-drop-shadow-0"]');
        filters.forEach((filter, index) => {
            if (index > 0) {
                const newId = generateUniqueId('highcharts-drop-shadow-0');
                filter.setAttribute('id', newId);

                const references = document.querySelectorAll(`svg [filter="url(#highcharts-drop-shadow-0)"]`);
                references.forEach((reference) => {
                    jq(reference).attr('filter', `url(#${newId})`);
                });
            }
        });

        const specificElements = ['highcharts-3yiiuel-0', 'highcharts-pu44erb-0'];
        specificElements.forEach((baseId, index) => {
            const element = document.querySelector(`#${baseId}`);
            if (element && !element.getAttribute('aria-label')) {
                element.setAttribute('aria-label', `Unique Chart Specific ${index + 1}`);
            }
        });

        resolve();
    });
}

// Call the function after the DOM has fully loaded
document.addEventListener('DOMContentLoaded', () => {
    assignUniqueAttributes();
});

/**
 * Page Type: QMod - Historical Data
 * 
 * Creates and inserts a label for an input selected by a specific CSS selector.
 * 
 * @since QMod v1.69.0
 */
function createLabelForInputBySelector(selector, labelText) {
    let inputElement = document.querySelector(selector);
    if (!inputElement) {
        return;
    }

    let inputId = 'input-' + Math.random().toString(36).substr(2, 9);
    inputElement.id = inputId;

    let label = document.createElement('label');
    label.htmlFor = inputId;
    label.textContent = labelText;
    label.classList.add("visually-hidden");

    inputElement.parentNode.insertBefore(label, inputElement);
}

// Create and insert labels using the specific QMod selectors after delay
delay(2000).then(() => {
    createLabelForInputBySelector('#qmod-fromdate > div > div.duet-date__input-wrapper > input.duet-date__input', 'From date');
    createLabelForInputBySelector('#qmod-todate > div > div.duet-date__input-wrapper > input.duet-date__input', 'To date');
});

/**
 * Page Type: QMod - Historical Data
 * 
 * Adds a span element with class "visually-hidden" to all matching th elements.
 * 
 * @since QMod v1.69.0
 */
function addSrOnlySpanToThElements() {
    // Select all th QMod elements with the specified class
    let thElements = document.querySelectorAll('th.qmod-control.control.sorting_disabled.dtr-hidden');

    // Iterate over each th element
    thElements.forEach(function(th) {
        // Check if there's already an visually-hidden span, if not, add one
        if (!th.querySelector('.visually-hidden')) {
            let span = document.createElement('span');
            span.className = 'visually-hidden';
            span.textContent = 'Control Header';
            span.setAttribute('aria-hidden', 'true');
    
            th.appendChild(span);
        }
    });
}

// Call the SR Only Function after delay
delay(2000).then(() => {
    addSrOnlySpanToThElements();
});

/**
 * Observes for content and resolves the promise once content is found.
 */
function observeForContent(selector) {
    return new Promise((resolve, reject) => {
        let checkInterval = setInterval(() => {
            if (jq(selector).html()) {
                clearInterval(checkInterval);
                resolve();
            }
        }, 100); // Check every 100 milliseconds
    });
}

/**
 * Page Type: QMod - Historical Data
 * 
 * QMOD table header SR issue fix for sorting headers.
 * 
 * @since QMod v1.69.0
 */
if (jq('body').hasClass('page-type-qmod---historical-data')) {
    observeForContent('#qmod-price-history').then(() => {
        // Append visually-hidden caption to table
        let $historyTable = jq('#qmod-price-history');
        $historyTable.prepend('<caption class="visually-hidden">Column headers are sortable.</caption>');
        
        // Remove aria-labels on sorting headers
        $historyTable.find('th.sorting').each(function() {
            jq(this).removeAttr('aria-label');
            // Remove aria-labels again on click, as they get added by qmod
            jq(this).accessibleClick(function (e) {
                jq('#qmod-price-history th.sorting').removeAttr('aria-label');
            });
        });
    });
}

/**
 * Global for all QMod Components with Legends
 * 
 * Sets tabindex and accessible click for .qmod-legend li elements.
 */
delay(1000).then(() => {
    jq('.qmod-legend li').each(function(i) {
        jq(this).attr('tabindex', 0);
        jq(this).accessibleClick(function(evt) {
            // We want to use the accessibleClick method so that the logic surrounding
            // keys is consistent; However, avoid recursion by causing an infinite click loop
            if (evt.type != 'click') {
                jq(this).click();
                return false;
            }
        });
    });
});

/**
 * Global for all QMod Components with Main Chart
 * 
 * Fixes the unique label for qmod-main-chart.
 */
function assignUniqueLabels() {
    return new Promise((resolve) => {
        const elements = document.querySelectorAll('.qmod-main-chart');

        elements.forEach((element, index) => {
            if (!element.getAttribute('aria-label')) {
                element.setAttribute('aria-label', `Unique Chart ${index + 1}`);
            }
        });

        resolve();
    });
}

// Call the function after the DOM has fully loaded
document.addEventListener('DOMContentLoaded', () => {
    assignUniqueLabels();
});

/**
 * Page Type: QMod - Dividends
 * 
 * Fixes invalid ARIA attribute names.
 * 
 * @since QMod v1.69.0
 */
function fixInvalidAriaAttributes() {
    return new Promise((resolve) => {
        const invalidAriaElements = document.querySelectorAll('[aria-role]');
        
        invalidAriaElements.forEach(element => {
            const ariaRoleValue = element.getAttribute('aria-role');
            element.removeAttribute('aria-role');
            if (ariaRoleValue && !element.getAttribute('role')) {
                element.setAttribute('role', ariaRoleValue);
            }
        });

        resolve();
    });
}

function observeInvalidAriaAttributes() {
    const observer = new MutationObserver(() => {
        fixInvalidAriaAttributes();
    });
    observer.observe(document.documentElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['aria-role']
    });
    fixInvalidAriaAttributes();
}

if (document.documentElement) {
    observeInvalidAriaAttributes();
} else {
    document.addEventListener('DOMContentLoaded', observeInvalidAriaAttributes);
}

jq(document).on('init.dt draw.dt', function () {
    fixInvalidAriaAttributes();
});

/**
 * Page Type: QMod - Chart, Quote & Historical 
 * 
 * Toggles the tabindex and aria-hidden attributes of elements in the tab panels 
 * to prevent tabbing into hidden fields.
 * 
 * @since QMod v1.69.0
 */
function toggleTabIndex(event, isFirstLoad = false) {
    let next, previous;
    if (!isFirstLoad) {
        next = jq(event.target.hash);
        previous = jq(event.relatedTarget.hash);
    } else {
        next = jq('#tab_quote');
        previous = jq('#tab_historical');
    }

    // Add aria-hidden="true" and tabindex="-1" to the hidden tab-panel
    previous.attr('aria-hidden', 'true');
    previous.find('input, button:not(.no-tab), th.sorting, .qmod-calendar-control, .highcharts-container, .qmod-a11y-toggle-wrap, .qmod-a11y-dropdown2-combobox, .highcharts-exit-anchor').attr('tabindex', '-1');
    previous.find('.highcharts-exit-anchor').attr('aria-hidden', 'true');

    // Remove aria-hidden and set tabindex="0" from the active tab-panel
    next.removeAttr('aria-hidden');
    next.find('input, button:not(.no-tab), th.sorting, .qmod-calendar-control, .highcharts-container, .qmod-a11y-toggle-wrap, .qmod-a11y-dropdown2-combobox, .highcharts-exit-anchor').attr('tabindex', '0');
    next.find('.highcharts-exit-anchor').attr('aria-hidden', 'false');
}

// Update table inputs on tab click
jq('.stock-tab a[data-bs-toggle="tab"]').on('shown.bs.tab', function (event) {
    toggleTabIndex(event, false);
});

// Initial load adjustments
delay(2000).then(() => {
    toggleTabIndex(null, true);
});

/**
 * Ensure dropdown in historical data pane opens on Enter key press
 */
jq(document).on('keydown', '.qmod-a11y-dropdown2', function (event) {
    if (event.key === 'Enter') {
        let expanded = jq(this).attr('aria-expanded') === 'true';
        jq(this).attr('aria-expanded', !expanded);
        jq(this).click();
    }
});

/**
 * Page Type: QMod - Chart, Quote & Historical 
 * 
 * Ensures that down and up arrow icons are not hidden and have the appropriate aria-labels.
 */
function setAriaLabelsForIcons() {
    const downArrowIcon = jq('.qmod-change.qmod-ch-down .qmod-change-icon.fa.fa-arrow-circle-down');
    const upArrowIcon = jq('.qmod-change.qmod-ch-up .qmod-change-icon.fa.fa-arrow-circle-up');

    if (downArrowIcon.length > 0) {
        downArrowIcon.attr('aria-hidden', 'false');
        downArrowIcon.attr('aria-label', 'Down');
        downArrowIcon.attr('role', 'img');
        downArrowIcon.removeAttr('aria-disabled');
    }

    if (upArrowIcon.length > 0) {
        upArrowIcon.attr('aria-hidden', 'false');
        upArrowIcon.attr('aria-label', 'Up');
        upArrowIcon.attr('role', 'img');
        upArrowIcon.removeAttr('aria-disabled');
    }
}

// Call the function with a delay after the DOM has fully loaded
document.addEventListener('DOMContentLoaded', () => {
    setTimeout(() => {
        setAriaLabelsForIcons();
    }, 2000); // 2-second delay
});

/**
 * Page Type: QMod - Dividends
 *
 * Disables DataTables column sorting (header click asc/desc) on Dividend History.
 * QMod does not expose a param for this; we disable it after DataTables init.
 */
function disableDividendHistorySorting(tableEl) {
    const table = tableEl || document.querySelector('.dividends-data .qmod-dividends-history-table');
    if (!table || typeof jq === 'undefined' || !jq.fn.DataTable || !jq.fn.DataTable.isDataTable(table)) {
        return false;
    }

    const $table = jq(table);
    const dt = $table.DataTable();

    dt.settings()[0].aoColumns.forEach(function (col) {
        col.bSortable = false;
    });

    $table.find('thead th').each(function () {
        const $th = jq(this);
        $th.off('click.DT keydown.DT mousedown.DT')
            .on('click.dividendNoSort keydown.dividendNoSort', function (e) {
                e.stopImmediatePropagation();
            })
            .removeClass('sorting sorting_asc sorting_desc')
            .addClass('sorting_disabled')
            .removeAttr('aria-sort')
            .removeAttr('tabindex')
            .removeAttr('aria-label');

        const $btn = $th.children('button');
        if ($btn.length) {
            $btn.replaceWith(
                jq('<span></span>')
                    .attr('class', $btn.attr('class') || '')
                    .text($btn.text())
            );
        }
    });

    return true;
}

jq(document).on('init.dt', function (e, settings) {
    fixInvalidAriaAttributes();
    const table = settings && settings.nTable;
    if (table && table.classList.contains('qmod-dividends-history-table')) {
        disableDividendHistorySorting(table);
    }
});

/**
 * Page Type: QMod - Dividends
 * 
 * Wraps the pagination in a nav element with class "pagination-wrapper" and 
 * sets appropriate aria-labels for the nav and pagination buttons.
 */
function enhancePaginationAccessibility() {
    const pagination = document.querySelector('#DataTables_Table_0_paginate');
    
    if (pagination) {
        // Track current page to detect actual page changes
        let currentPage = null;
        let isScrolling = false;
        
        // Function to get current page number
        function getCurrentPage() {
            const currentButton = pagination.querySelector('.paginate_button.current');
            return currentButton ? currentButton.textContent.trim() : null;
        }
        
        // Initialize current page
        currentPage = getCurrentPage();
        
        // Function to scroll to top of the dividends table and set focus
        function scrollToTableTop() {
            if (isScrolling) return;
            isScrolling = true;
            
            const newPage = getCurrentPage();
            
            // Only proceed if page actually changed
            if (newPage !== currentPage) {
                currentPage = newPage;
                
                const dividendsTable = document.querySelector('.dividends-data');
                if (dividendsTable) {
                    dividendsTable.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'start' 
                    });
                    
                    // Set focus to the "Dividend History" title for context
                    setTimeout(() => {
                        try {
                            // Find the "Dividend History" heading
                            const dividendHistoryHeading = document.querySelector('.qmod-dividends-history .qmod-heading');
                            if (dividendHistoryHeading) {
                                // Make the heading focusable and set focus
                                dividendHistoryHeading.setAttribute('tabindex', '-1');
                                dividendHistoryHeading.focus();
                            }
                        } finally {
                            isScrolling = false;
                        }
                    }, 300); // Delay to ensure smooth scroll completes and table content is updated
                } else {
                    isScrolling = false;
                }
            } else {
                isScrolling = false;
            }
        }

        // Add accessible names to both dividend tables
        function addTableAccessibleName() {
            // Handle Dividend History table (now DataTables_Table_0 after Upcoming Dividends removal)
            const historyTable = document.querySelector('#DataTables_Table_0');
            const dividendHistoryHeading = document.querySelector('.qmod-dividends-history .qmod-heading');
            
            if (historyTable && dividendHistoryHeading && !historyTable.hasAttribute('aria-labelledby')) {
                // Use aria-labelledby to reference the heading
                const headingId = 'dividend-history-heading';
                dividendHistoryHeading.setAttribute('id', headingId);
                historyTable.setAttribute('aria-labelledby', headingId);
            }
        }

        // Create a nav element with the class "pagination-wrapper" and aria-label "Pagination"
        const navWrapper = document.createElement('nav');
        navWrapper.classList.add('pagination-wrapper');
        navWrapper.setAttribute('aria-label', 'Pagination');

        // Wrap the pagination element with the nav element
        pagination.parentNode.insertBefore(navWrapper, pagination);
        navWrapper.appendChild(pagination);

        // Function to set aria-labels for the next and previous buttons
        function setAriaLabels() {
            const prevButton = pagination.querySelector('.previous');
            const nextButton = pagination.querySelector('.next');
            
            if (prevButton) {
                prevButton.setAttribute('aria-label', 'Previous Page');
                // Fixes aria-prohibited-attr violation
                prevButton.setAttribute('role', 'link');
            }
            
            if (nextButton) {
                nextButton.setAttribute('aria-label', 'Next Page');
                // Fixes aria-prohibited-attr violation
                nextButton.setAttribute('role', 'link');
            }

            // Set aria-current="page" for the active page
            const pageButtons = pagination.querySelectorAll('.paginate_button');
            pageButtons.forEach(button => {
                if (button.classList.contains('current')) {
                    button.setAttribute('aria-current', 'page');
                } else {
                    button.removeAttribute('aria-current');
                }
            
                if (!button.classList.contains('previous') && !button.classList.contains('next')) {
                    const blabel = parseInt(button.getAttribute('data-dt-idx'), 10) + 1;
                    button.setAttribute('aria-label', 'Page ' + blabel);
                }
            
                // Fix aria-role attribute (convert to proper role attribute)
                const ariaRoleValue = button.getAttribute('aria-role');
                if (ariaRoleValue) {
                    button.removeAttribute('aria-role');
                    button.setAttribute('role', ariaRoleValue);
                }
                
            });
            
            // Add screen reader text to ellipses instead of prohibited aria-label
            const ellipses = pagination.querySelectorAll('.ellipsis');
            ellipses.forEach(ellipsis => {
                // Check if visually-hidden span already exists to avoid duplicates
                if (!ellipsis.querySelector('.visually-hidden')) {
                    const srOnlySpan = document.createElement('span');
                    srOnlySpan.className = 'visually-hidden';
                    srOnlySpan.textContent = 'Ellipsis indicating non-visible pages';
                    ellipsis.appendChild(srOnlySpan);
                }
            });
        };

        // Add accessible names to both dividend tables
        function addTableAccessibleName() {
            // Handle Dividend History table (now DataTables_Table_0 after Upcoming Dividends removal)
            const historyTable = document.querySelector('#DataTables_Table_0');
            const dividendHistoryHeading = document.querySelector('.qmod-dividends-history .qmod-heading');
            
            if (historyTable && dividendHistoryHeading && !historyTable.hasAttribute('aria-labelledby')) {
                // Use aria-labelledby to reference the heading
                const headingId = 'dividend-history-heading';
                dividendHistoryHeading.setAttribute('id', headingId);
                historyTable.setAttribute('aria-labelledby', headingId);
            }
        }

        // Apply aria-labels initially
        setAriaLabels();
        
        // Add table accessibility initially
        addTableAccessibleName();

        // Simple MutationObserver for aria labels and table accessibility only
        const observer = new MutationObserver(() => {
            setAriaLabels();
            addTableAccessibleName(); // Ensure table accessibility is maintained
        });
        observer.observe(pagination, { childList: true, subtree: true });

        // Use event delegation to handle pagination clicks and keyboard events
        pagination.addEventListener('click', (e) => {
            const button = e.target.closest('.paginate_button:not(.disabled)');
            if (button && !button.classList.contains('current')) {
                setTimeout(scrollToTableTop, 200);
            }
        });

        // Also listen for keyboard events (Enter/Space)
        pagination.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                const button = e.target.closest('.paginate_button:not(.disabled)');
                if (button && !button.classList.contains('current')) {
                    setTimeout(scrollToTableTop, 200);
                }
            }
        });

    }
}

// Call the function with a delay after the DOM has fully loaded
document.addEventListener('DOMContentLoaded', () => {
    delay(2000).then(() => {
        enhancePaginationAccessibility();
        disableDividendHistorySorting();
    });
});

/**
 * Page Type: QMod - Chart, Quote & Historical 
 * 
 * Adds meaningful aria-label to empty table headers.
 * 
 * @since QMod v1.69.0
 */
function fixEmptyTableHeaders() {
    return new Promise((resolve) => {
        const emptyHeaders = document.querySelectorAll('th.qmod-control.control.sorting_disabled[aria-label=""]');
        emptyHeaders.forEach(header => {
            header.setAttribute('aria-label', 'Empty header');
        });
        resolve();
    });
}

// Call the function after the DOM has fully loaded
document.addEventListener('DOMContentLoaded', () => {
    fixEmptyTableHeaders();
});

/**
 * Page Type: QMod - Chart, Quote & Historical 
 * 
 * Adds a valid role to .qmod-a11y-toggle-wrap.qmod-volume-toggle elements.
 * 
 * @since QMod v1.69.0
 */
function addTabIndexToVolumeToggle() {
    return new Promise((resolve) => {
        const toggleWrapElements = document.querySelectorAll('.qmod-a11y-toggle-wrap.qmod-volume-toggle');
        toggleWrapElements.forEach(element => {
            element.setAttribute('tabindex', '-1');
        });
        resolve();
    });
}

// Call the function after the DOM has fully loaded
document.addEventListener('DOMContentLoaded', () => {
    addTabIndexToVolumeToggle();
});